import ApiService from '../services/api';
import {
  ADMIN_TOKEN_KEY,
  LEGACY_ACCESS_TOKEN_KEY,
  MARKETING_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  USER_TOKEN_KEY
} from './authTokens';

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';

let initialized = false;

export const initAnalytics = () => {
  if (!GA_MEASUREMENT_ID || initialized || typeof window === 'undefined') {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };

  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`;
  document.head.appendChild(script);

  initialized = true;
};

export const trackPageView = (path) => {
  if (!GA_MEASUREMENT_ID || typeof window === 'undefined') {
    return;
  }

  if (!initialized) {
    initAnalytics();
  }

  if (typeof window.gtag === 'function') {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: path,
      page_location: window.location.href
    });
  }
};

// Push a custom event to the GTM dataLayer (GTM-TTLNSHRK routes it to GA4 and Google Ads).
// Never include PII (names, tokens) in params. The one exception is user_email on
// sign_up, which Google Ads enhanced conversions hashes before sending.
export const pushDataLayerEvent = (event, params = {}) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...params });
};

// ---------------------------------------------------------------------------
// Google Ads conversions: each fires at most once per key on this browser, so
// reloads, re-renders, retries and revisits of a success page never repeat them.
// ---------------------------------------------------------------------------

export const CONVERSION_ONCE_PREFIX = 'jelani_conversion_once:';
const conversionKeysInMemory = new Set();

export const isConversionMarked = (onceKey) => {
  if (conversionKeysInMemory.has(onceKey)) {
    return true;
  }
  try {
    return Boolean(window.localStorage.getItem(`${CONVERSION_ONCE_PREFIX}${onceKey}`));
  } catch (error) {
    return false;
  }
};

export const markConversion = (onceKey) => {
  conversionKeysInMemory.add(onceKey);
  try {
    window.localStorage.setItem(`${CONVERSION_ONCE_PREFIX}${onceKey}`, '1');
  } catch (error) {
    // Storage unavailable; the in-memory mark still covers this page load.
  }
};

// Returns true when the event was pushed, false when this key already fired.
export const pushDataLayerEventOnce = (onceKey, event, params = {}) => {
  if (!onceKey || isConversionMarked(onceKey)) {
    return false;
  }
  markConversion(onceKey);
  pushDataLayerEvent(event, params);
  return true;
};

// Fires sign_up after POST /api/auth/verify-email/ succeeded: the backend creates
// the account only then (registration just stores pending data).
export const trackSignUp = (user) => {
  try {
    if (!user?.user_id) {
      return;
    }
    pushDataLayerEventOnce(`sign_up:${user.user_id}`, 'sign_up', {
      method: 'email',
      ...(user.email ? { user_email: user.email } : {})
    });
  } catch (error) {
    // Tracking must never break the app.
  }
};

const contractCount = (response) => Number(response?.count ?? response?.pagination?.count);
const contractResults = (response) => response?.results || response?.contracts || [];

// Fires contract_upload when the server confirms the just-uploaded contract is
// the user's only one. Existing users (count > 1) are marked so the list is not
// re-checked on later uploads.
export const trackFirstContractUpload = async (userId) => {
  try {
    const onceKey = `contract_upload:${userId}`;
    if (!userId || isConversionMarked(onceKey)) {
      return;
    }
    const count = contractCount(await ApiService.getContracts({ page: 1, pageSize: 1 }));
    if (count === 1) {
      pushDataLayerEventOnce(onceKey, 'contract_upload');
    } else if (count > 1) {
      markConversion(onceKey);
    }
  } catch (error) {
    // Tracking must never break the app.
  }
};

const MAX_CONTRACTS_CHECKED = 100;

// Fires analysis_completed when a contract's analysis finished with status
// 'completed' and the user has no other completed contract.
export const trackFirstAnalysisCompleted = async (userId, contractId) => {
  try {
    const onceKey = `analysis_completed:${userId}`;
    if (!userId || !contractId || isConversionMarked(onceKey)) {
      return;
    }
    const response = await ApiService.getContracts({ page: 1, pageSize: MAX_CONTRACTS_CHECKED });
    const contracts = contractResults(response);
    const otherCompleted = contracts.some(
      (c) => String(c.contract_id) !== String(contractId) && c.status === 'completed'
    );
    // Too many contracts to inspect: this cannot be the first analysis.
    if (otherCompleted || contractCount(response) > contracts.length) {
      markConversion(onceKey);
      return;
    }
    pushDataLayerEventOnce(onceKey, 'analysis_completed');
  } catch (error) {
    // Tracking must never break the app.
  }
};

// Fires purchase for a Stripe Checkout session the backend verified as paid.
export const trackPurchase = ({ transactionId, value, currency }) => {
  try {
    const amount = Number(value);
    if (!transactionId || !Number.isFinite(amount) || amount <= 0) {
      return;
    }
    pushDataLayerEventOnce(`purchase:${transactionId}`, 'purchase', {
      value: amount,
      currency: String(currency || 'AED').toUpperCase(),
      transaction_id: String(transactionId)
    });
  } catch (error) {
    // Tracking must never break the app.
  }
};

// ---------------------------------------------------------------------------
// First-party page view tracking (POST /api/analytics/track/).
// Uses a random visitor id (localStorage) and a session id (sessionStorage)
// that rotates after 30 minutes of inactivity. No personal data is sent.
//
// Only public marketing pages are tracked, and nothing is sent while anyone is
// signed in (customer, admin or marketing staff): using the product is not
// marketing traffic. The backend enforces the same allowlist
// (jelaniai-be/apps/analytics/paths.py); keep both in sync with App.js routes.
// ---------------------------------------------------------------------------

export const VISITOR_ID_KEY = 'jelani_visitor_id';
export const SESSION_ID_KEY = 'jelani_session_id';
export const SESSION_LAST_ACTIVITY_KEY = 'jelani_session_last_activity';
export const SESSION_IDLE_TIMEOUT_MS = 30 * 60 * 1000;

const MAX_PATH_LENGTH = 255;
const MAX_REFERRER_LENGTH = 1000;
const MAX_UTM_LENGTH = 100;
export const PUBLIC_PATHS = [
  '/',
  '/features',
  '/pricing',
  '/resources',
  '/about',
  '/contact',
  '/contact-sales',
  '/terms',
  '/code-of-conduct',
  '/ethics',
  '/privacy-policy',
  '/blog',
  '/login',
  '/signup'
];
const BLOG_POST_PATH_RE = /^\/blog\/[a-z0-9]+(?:-[a-z0-9]+)*$/;
const SIGNED_IN_TOKEN_KEYS = [
  USER_TOKEN_KEY,
  LEGACY_ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  ADMIN_TOKEN_KEY,
  MARKETING_TOKEN_KEY
];
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign'];

// document.referrer and the landing URL's UTM tags describe how this page load
// started. They are sent only with its first tracked view: a session that
// starts later in the same tab (after 30 idle minutes) did not come from them.
let attributionSent = false;
export const resetPageLoadAttribution = () => {
  attributionSent = false;
};

// Time on site: page views alone never tell how long someone stayed on the
// last page, so an engagement ping (event: 'ping') is sent when the page is
// hidden or closed, and once ENGAGEMENT_PING_DELAY_MS after each view in case
// the leave ping is lost. Pings only extend the current session's duration;
// the dashboard counts a session shorter than 10s as a bounce.
export const ENGAGEMENT_PING_DELAY_MS = 10 * 1000;
let lastTrackedView = null;
let engagementTimer = null;
let leaveListenersInstalled = false;

export const sendEngagementPing = () => {
  try {
    if (!lastTrackedView || isSignedIn()) {
      return;
    }
    Promise.resolve(ApiService.trackPageView({ ...lastTrackedView, event: 'ping' })).catch(() => {});
  } catch (error) {
    // Tracking must never break the app.
  }
};

const onVisibilityChange = () => {
  if (document.visibilityState === 'hidden') {
    sendEngagementPing();
  }
};

const startEngagementTracking = (view) => {
  lastTrackedView = view;
  clearTimeout(engagementTimer);
  engagementTimer = setTimeout(sendEngagementPing, ENGAGEMENT_PING_DELAY_MS);
  if (!leaveListenersInstalled && typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', sendEngagementPing);
    leaveListenersInstalled = true;
  }
};

export const resetEngagementTracking = () => {
  clearTimeout(engagementTimer);
  engagementTimer = null;
  lastTrackedView = null;
};

// In-memory fallbacks used when browser storage is unavailable.
const memoryIds = { visitorId: null, sessionId: null, lastActivity: 0 };

export const generateUuid = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  // RFC 4122 version 4 / variant bits
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

// The visitor id already in use, without creating one (empty when this browser was never tracked).
export const getExistingVisitorId = () => {
  try {
    return window.localStorage.getItem(VISITOR_ID_KEY) || memoryIds.visitorId || '';
  } catch (error) {
    return memoryIds.visitorId || '';
  }
};

const getVisitorId = () => {
  try {
    let visitorId = window.localStorage.getItem(VISITOR_ID_KEY);
    if (!visitorId) {
      visitorId = generateUuid();
      window.localStorage.setItem(VISITOR_ID_KEY, visitorId);
    }
    return visitorId;
  } catch (error) {
    if (!memoryIds.visitorId) {
      memoryIds.visitorId = generateUuid();
    }
    return memoryIds.visitorId;
  }
};

const getSessionId = (now) => {
  try {
    let sessionId = window.sessionStorage.getItem(SESSION_ID_KEY);
    const lastActivity = Number(window.sessionStorage.getItem(SESSION_LAST_ACTIVITY_KEY)) || 0;
    if (!sessionId || now - lastActivity > SESSION_IDLE_TIMEOUT_MS) {
      sessionId = generateUuid();
      window.sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    }
    window.sessionStorage.setItem(SESSION_LAST_ACTIVITY_KEY, String(now));
    return sessionId;
  } catch (error) {
    if (!memoryIds.sessionId || now - memoryIds.lastActivity > SESSION_IDLE_TIMEOUT_MS) {
      memoryIds.sessionId = generateUuid();
    }
    memoryIds.lastActivity = now;
    return memoryIds.sessionId;
  }
};

export const getUtmParams = (search = '') => {
  const utm = {};
  try {
    const params = new URLSearchParams(search);
    UTM_PARAMS.forEach((key) => {
      const value = params.get(key);
      if (value) {
        utm[key] = value.slice(0, MAX_UTM_LENGTH);
      }
    });
  } catch (error) {
    // Ignore malformed query strings
  }
  return utm;
};

// Same normalization as the backend: no query/fragment, lowercase, no trailing slash.
export const normalizeTrackedPath = (path = '') => {
  const pathname = String(path || '').split(/[?#]/)[0].toLowerCase();
  return pathname.length > 1 ? (pathname.replace(/\/+$/, '') || '/') : pathname;
};

export const shouldTrackFirstPartyPath = (path = '') => (
  typeof path === 'string' &&
  (PUBLIC_PATHS.includes(path) || BLOG_POST_PATH_RE.test(path))
);

// Any stored token means someone is signed in on this browser. Stale tokens
// count too: they belong to people who already have an account.
export const isSignedIn = () => {
  try {
    return SIGNED_IN_TOKEN_KEYS.some((key) => Boolean(window.localStorage.getItem(key)));
  } catch (error) {
    return false;
  }
};

export const trackFirstPartyPageView = (path) => {
  try {
    if (typeof window === 'undefined') {
      return;
    }

    const pathname = normalizeTrackedPath(path).slice(0, MAX_PATH_LENGTH);
    if (!shouldTrackFirstPartyPath(pathname) || isSignedIn()) {
      return;
    }

    const now = Date.now();
    const firstOfPageLoad = !attributionSent;
    attributionSent = true;
    const payload = {
      visitor_id: getVisitorId(),
      session_id: getSessionId(now),
      path: pathname,
      referrer: firstOfPageLoad && typeof document !== 'undefined'
        ? (document.referrer || '').slice(0, MAX_REFERRER_LENGTH)
        : '',
      ...(firstOfPageLoad ? getUtmParams(window.location?.search || '') : {}),
    };

    Promise.resolve(ApiService.trackPageView(payload)).catch(() => {});
    startEngagementTracking({ visitor_id: payload.visitor_id, session_id: payload.session_id, path: pathname });
  } catch (error) {
    // Tracking must never break the app.
  }
};
